import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { doc, onSnapshot } from 'firebase/firestore';
import nativeFirestore from '@react-native-firebase/firestore';
import { db } from '../services/firebaseConfig';
import { Colors, SIZES } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { volver } from '../utils/volver';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL CANDIDATO APROBADO Y LO QUE LE FALTA PARA TRABAJAR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── EL HUECO QUE CIERRA ──────────────────────────────────────────────────
 *
 * El candidato llenaba el enlace mágico, el dueño lo aprobaba, recogía el
 * carro… y en la app NO TENÍA CÓMO RECIBIR UN VIAJE. Le faltaban tres cosas y
 * nada se las decía:
 *
 *   · Su rol seguía siendo `CAR_RENTER`, cuyas pestañas son Marketplace, Chats
 *     y Perfil. No tiene el mapa del chofer.
 *   · No estaba marcado como chofer verificado (`isDriverVerified` solo se lo
 *     pone `reviewKycSubmission` a los roles de chofer, functions/src/kyc.ts).
 *   · Y el carro del dueño no está en SU lista de vehículos: un chofer solo
 *     puede ponerse en línea con un vehículo en estado APROBADO
 *     (functions/src/vehiculos.ts).
 *
 * Para trabajar tenía que descubrir por su cuenta que debía cambiarse a
 * «Chofer», rehacer el KYC de conductor y registrar ese vehículo. Nada en todo
 * el camino del enlace mágico lo avisaba ni lo encaminaba. El dueño le entregó
 * las llaves y el chofer se quedó mirando una app que no le deja trabajar.
 *
 * ── POR QUÉ NO SE LE CONCEDE TODO AUTOMÁTICAMENTE ───────────────────────
 *
 * Sería más cómodo marcarlo chofer verificado al aprobarlo, y es justo lo que
 * no se puede hacer: la verificación de chofer y la aprobación del vehículo son
 * las dos comprobaciones que protegen al PASAJERO que se sube al carro, y las
 * hace Go Safely, no el dueño del rent car. Saltárselas porque un particular
 * dijo que sí sería poner a alguien a dar viajes sin que nadie de la plataforma
 * haya mirado nada.
 *
 * Lo que sí se puede quitar es la adivinanza. Esta pantalla le dice en qué
 * punto está, qué carro le tocó y exactamente qué le queda, con el botón de
 * cada paso. Eso era lo que faltaba.
 */
type Postulacion = {
  estado?: string;
  ownerId?: string;
  vehiculo?: string;
  placa?: string | null;
  motivo?: string | null;
};

export default function MiPostulacionScreen() {
  const router = useRouter();
  const uid = useAppStore((s) => s.userFirebaseUid);
  const userRole = useAppStore((s) => s.userRole);
  const isDriverVerified = useAppStore((s) => s.isDriverVerified);

  const [postulacion, setPostulacion] = useState<Postulacion | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!uid) {
      setCargando(false);
      return;
    }
    const alLlegar = (datos: any) => {
      setPostulacion((datos?.postulacionRentCar ?? null) as Postulacion | null);
      setCargando(false);
    };
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('users')
        .doc(uid)
        .onSnapshot(
          (s: any) => alLlegar(s?.exists?.() ? s.data() : null),
          (e: any) => {
            console.warn('[mi-postulacion]', e?.message);
            setCargando(false);
          }
        );
    }
    return onSnapshot(
      doc(db, 'users', uid),
      (s) => alLlegar(s.exists() ? s.data() : null),
      (e) => {
        console.warn('[mi-postulacion] web:', e?.message);
        setCargando(false);
      }
    );
  }, [uid]);

  const estado = String(postulacion?.estado ?? '').toUpperCase();
  const yaEsChofer = userRole === 'DRIVER';

  const Paso = ({
    hecho,
    titulo,
    detalle,
    boton,
    onPress,
  }: {
    hecho: boolean;
    titulo: string;
    detalle: string;
    boton?: string;
    onPress?: () => void;
  }) => (
    <View style={[styles.paso, hecho && { borderColor: '#00C853', backgroundColor: 'rgba(0,200,83,0.06)' }]}>
      <Ionicons
        name={hecho ? 'checkmark-circle' : 'ellipse-outline'}
        size={24}
        color={hecho ? '#00C853' : Colors.textSecondary}
        style={{ marginRight: 12, marginTop: 2 }}
      />
      <View style={{ flex: 1 }}>
        <Text style={[styles.pasoTitulo, hecho && { color: '#2E7D32' }]}>{titulo}</Text>
        <Text style={styles.pasoDetalle}>{detalle}</Text>
        {!hecho && !!boton && (
          <TouchableOpacity style={styles.pasoBoton} onPress={onPress}>
            <Text style={styles.pasoBotonTexto}>{boton}</Text>
            <Ionicons name="arrow-forward" size={16} color="#FFF" style={{ marginLeft: 6 }} />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.safe}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => volver(router)} style={styles.back}>
          <Ionicons name="arrow-back" size={24} color={Colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Mi solicitud</Text>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView contentContainerStyle={styles.cuerpo}>
        {cargando ? (
          <Text style={styles.gris}>Cargando…</Text>
        ) : !postulacion ? (
          <View style={styles.tarjeta}>
            <Ionicons name="document-text-outline" size={44} color={Colors.border} />
            <Text style={styles.tarjetaTitulo}>Todavía no has enviado ninguna solicitud</Text>
            <Text style={styles.gris}>
              Busca un vehículo en el Marketplace y escríbele al dueño. Cuando lleguen a un acuerdo
              te manda un enlace para enviarle tus documentos.
            </Text>
            <TouchableOpacity style={styles.pasoBoton} onPress={() => router.push('/(tabs)/marketplace' as any)}>
              <Text style={styles.pasoBotonTexto}>Ver vehículos</Text>
            </TouchableOpacity>
          </View>
        ) : estado === 'RECHAZADO' ? (
          <View style={styles.tarjeta}>
            <Ionicons name="close-circle" size={44} color={Colors.error} />
            <Text style={styles.tarjetaTitulo}>El dueño no pudo aceptarte</Text>
            <Text style={styles.gris}>
              {postulacion?.motivo
                ? postulacion.motivo
                : 'No te dio un motivo. Puedes buscar otro vehículo en el Marketplace: hay más dueños.'}
            </Text>
            <TouchableOpacity style={styles.pasoBoton} onPress={() => router.push('/(tabs)/marketplace' as any)}>
              <Text style={styles.pasoBotonTexto}>Buscar otro vehículo</Text>
            </TouchableOpacity>
          </View>
        ) : estado === 'APROBADO' ? (
          <>
            <View style={[styles.tarjeta, { borderColor: '#00C853', borderWidth: 2 }]}>
              <Ionicons name="key" size={44} color="#00C853" />
              <Text style={styles.tarjetaTitulo}>¡Te aprobaron!</Text>
              <Text style={styles.vehiculo}>{postulacion?.vehiculo ?? 'Tu vehículo'}</Text>
              {!!postulacion?.placa && <Text style={styles.gris}>Placa {postulacion.placa}</Text>}
              <Text style={[styles.gris, { marginTop: 10 }]}>
                Acuerda con el dueño cuándo recoger el carro. Escríbele por el chat.
              </Text>
            </View>

            <Text style={styles.seccion}>Te faltan {(yaEsChofer ? 0 : 1) + (isDriverVerified ? 0 : 1) + 1} pasos para recibir viajes</Text>
            <Text style={[styles.gris, { marginBottom: 14 }]}>
              Son de Go Safely, no del dueño: nosotros comprobamos a cada chofer y cada vehículo
              antes de que lleve a un pasajero.
            </Text>

            <Paso
              hecho={yaEsChofer}
              titulo="Cambiarte a Chofer"
              detalle="Ahora mismo tu cuenta es de alguien que busca carro. Para recibir viajes tienes que estar como Chofer."
              boton="Hacerme chofer"
              onPress={() => router.push('/kyc-welcome' as any)}
            />
            <Paso
              hecho={isDriverVerified}
              titulo="Verificarte como chofer"
              detalle="Tu cédula y tu licencia las revisa una persona de nuestro equipo. Si ya las enviaste, está en revisión."
              boton="Enviar mis documentos"
              onPress={() => router.push('/kyc-welcome' as any)}
            />
            <Paso
              hecho={false}
              titulo="Registrar el vehículo que vas a manejar"
              detalle={`Registra el ${postulacion?.vehiculo ?? 'carro'} a tu nombre con su placa y sus fotos. Lo aprobamos nosotros antes de que puedas ponerte en línea.`}
              boton="Registrar el vehículo"
              onPress={() => router.push('/agregar-vehiculo' as any)}
            />
          </>
        ) : (
          <View style={styles.tarjeta}>
            <Ionicons name="hourglass-outline" size={44} color="#FF9500" />
            <Text style={styles.tarjetaTitulo}>El dueño está revisando tus documentos</Text>
            <Text style={styles.gris}>
              Te avisamos en cuanto decida. Mientras tanto puedes escribirle por el chat si tienes
              alguna duda.
            </Text>
            <TouchableOpacity style={styles.pasoBoton} onPress={() => router.push('/(tabs)/rent-chats' as any)}>
              <Text style={styles.pasoBotonTexto}>Abrir el chat</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: Colors.background },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SIZES.padding,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  back: { padding: 4 },
  headerTitle: { fontSize: 18, fontWeight: '800', color: Colors.text },
  cuerpo: { padding: SIZES.padding, paddingBottom: 60 },
  tarjeta: {
    backgroundColor: Colors.card,
    borderRadius: 18,
    padding: 22,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.border,
    marginBottom: 20,
  },
  tarjetaTitulo: {
    fontSize: 19,
    fontWeight: '800',
    color: Colors.text,
    marginTop: 12,
    marginBottom: 6,
    textAlign: 'center',
  },
  vehiculo: { fontSize: 17, fontWeight: '700', color: Colors.primary, marginBottom: 2 },
  gris: { color: Colors.textSecondary, textAlign: 'center', lineHeight: 20 },
  seccion: { fontSize: 16, fontWeight: '800', color: Colors.text, marginBottom: 4 },
  paso: {
    flexDirection: 'row',
    backgroundColor: Colors.card,
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  pasoTitulo: { fontSize: 15, fontWeight: '700', color: Colors.text, marginBottom: 4 },
  pasoDetalle: { fontSize: 13, color: Colors.textSecondary, lineHeight: 19 },
  pasoBoton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: Colors.primary,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
    marginTop: 12,
  },
  pasoBotonTexto: { color: '#FFF', fontWeight: '700', fontSize: 14 },
});
